import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { api, apiError } from "../lib/api";
import { PageHeader, Card, Btn, Input, Label, Badge, Empty, Modal } from "../components/UI";
import { ComplianceChip } from "../components/ComplianceChip";
import { SlidePanel } from "../components/SlidePanel";
import { InfoTip } from "../components/InfoTip";
import { CocoaSelect } from "../components/CocoaSelect";
import { DateField } from "./engagement/DateField";
import { Search, Plus, X, ArrowUpDown, ArrowUp, ArrowDown, SquareArrowOutUpRight, Bookmark, Flame, Zap, SlidersHorizontal, Users, Activity, ShieldAlert, CalendarPlus, LogOut, Sparkles } from "lucide-react";
import { ClientQuickDrawer } from "../components/ClientQuickDrawer";
import { ExportCsvBtn } from "../components/ExportCsvBtn";
import { AvatarWithStatus } from "../components/StatusDot";
import { toast } from "sonner";

const PAGE_SIZE = 25;
const SORTS = { name: (c) => `${c.last_name} ${c.first_name}`.toLowerCase(), hrn: (c) => c.health_record_number || c.mrn || "", status: (c) => c.status || "", clinician: (c) => c._clinician || "", compliance: (c) => c._score ?? -1, priority: (c) => c._priority ?? 0, admitted: (c) => c.admission_date || "", last_seen: (c) => c.last_seen || "" };
const EMPTY_ADV = { gender: "", program: "", ageMin: "", ageMax: "", admittedFrom: "", admittedTo: "", lastSeenFrom: "", lastSeenTo: "" };

const STATUS_OPTS = [["active", "Active"], ["referred", "Referred"], ["inactive", "Inactive"], ["discharged", "Discharged"]];
const COMPLIANCE_OPTS = [["red", "Critical (below 50%)"], ["amber", "Needs work (50-79%)"], ["green", "Compliant (80%+)"]];
const PRIORITY_OPTS = [["high", "High priority"], ["medium", "Medium"], ["low", "Low"]];

const CONDITIONS = [
  ["allergies", "Has allergies on file"],
  ["missed_injection", "Missed injection"],
  ["med_refill_due", "Medication refill due (30d)"],
  ["tx_plan_due", "Treatment plan due/overdue"],
  ["unsigned_docs", "Has unsigned documents"],
  ["high_risk", "High-risk C-SSRS screen (90d)"],
  ["absent_7d", "Not seen in 7+ days"],
  ["missed_appt", "Missed an appointment"],
  ["no_upcoming_appt", "No upcoming appointment"],
  ["no_clinician", "No clinician assigned"],
  ["portal_active", "Client portal active"],
  ["expiring_consent", "Consent renewal due"],
];

const PRESETS = [
  { key: "injection-follow-up", name: "Injection Follow-up", set: { rules: [{ combo: "and", cond: "missed_injection" }] } },
  { key: "allergy-watch", name: "Allergy Watch", set: { status: "active", rules: [{ combo: "and", cond: "allergies" }] } },
  { key: "tx-plans-due", name: "Treatment Plans Due", set: { rules: [{ combo: "and", cond: "tx_plan_due" }] } },
  { key: "high-risk-watch", name: "High-Risk Watch", set: { rules: [{ combo: "and", cond: "high_risk" }] } },
  { key: "re-engagement", name: "Re-engagement", set: { status: "active", rules: [{ combo: "and", cond: "absent_7d" }, { combo: "or", cond: "missed_appt" }] } },
  { key: "chart-gaps", name: "Chart Gaps", set: { compliance: "red", rules: [] } },
  { key: "new-admissions", name: "New Admissions (30d)", set: { adv: { admittedFrom: () => dayjs().subtract(30, "day").format("YYYY-MM-DD") }, rules: [] } },
  { key: "unassigned", name: "Unassigned Clients", set: { rules: [{ combo: "and", cond: "no_clinician" }] } },
  { key: "no-upcoming", name: "No Upcoming Appt", set: { status: "active", rules: [{ combo: "and", cond: "no_upcoming_appt" }] } },
  { key: "portal-active", name: "Portal Active", set: { rules: [{ combo: "and", cond: "portal_active" }] } },
];

function Avatar({ c }) {
  const ring = c._plevel === "high" ? "ring-[#D32F2F]" : c._plevel === "medium" ? "ring-[#E65100]" : c.status === "active" ? "ring-[#2E7D32]" : "ring-[#D7CCC8]";
  const img = c.photo
    ? <img src={c.photo} alt="" className="h-8 w-8 rounded-full object-cover shrink-0" />
    : <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#4E342E] text-[10px] font-bold text-white">{c.first_name?.[0]}{c.last_name?.[0]}</span>;
  return (
    <span className={`inline-flex shrink-0 rounded-full ring-2 ring-offset-2 ring-offset-white ${ring}`} data-testid={`ac-avatar-${c.id}`}>
      <AvatarWithStatus status={c.status}>{img}</AvatarWithStatus>
    </span>
  );
}

export default function AllClients() {
  const navigate = useNavigate();
  const [clients, setClients] = useState(null);
  const [compliance, setCompliance] = useState({});
  const [priority, setPriority] = useState({});
  const [clinicians, setClinicians] = useState([]);
  const [flags, setFlags] = useState({});
  const [clientPrograms, setClientPrograms] = useState({});
  const [programList, setProgramList] = useState([]);
  const [views, setViews] = useState([]);
  const [activeView, setActiveView] = useState("all");
  const [saveOpen, setSaveOpen] = useState(false);
  const [viewName, setViewName] = useState("");
  const [search, setSearch] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fClinician, setFClinician] = useState("");
  const [fCompliance, setFCompliance] = useState("");
  const [fPriority, setFPriority] = useState("");
  const [adv, setAdv] = useState(EMPTY_ADV);
  const [rules, setRules] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sort, setSort] = useState({ key: "priority", dir: "desc" });
  const [page, setPage] = useState(1);
  const [quickClient, setQuickClient] = useState(null);
  const [priTip, setPriTip] = useState(null);

  useEffect(() => { document.title = "All Clients | COCOA EHR"; }, []);
  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/clients/compliance-scores").then((r) => setCompliance(r.data)).catch(() => {});
    api.get("/clients/priority", { params: { limit: 1000 } }).then((r) => {
      setPriority(Object.fromEntries(r.data.clients.map((c) => [c.id, c])));
    }).catch(() => {});
    api.get("/users").then((r) => setClinicians(r.data.filter((u) => u.role === "clinician" && !u.first_name.startsWith("TEST")))).catch(() => {});
    api.get("/saved-views", { params: { page: "clients" } }).then((r) => setViews(r.data)).catch(() => {});
    api.get("/clients/filter-flags").then((r) => { setFlags(r.data.flags || {}); setClientPrograms(r.data.programs || {}); }).catch(() => {});
    api.get("/programs").then((r) => setProgramList([...new Set(r.data.map((p) => p.name).filter(Boolean))])).catch(() => {});
  }, []);

  const applyView = (v) => {
    setActiveView(v.id);
    const c = v.config || {};
    setSearch(c.search || ""); setFStatus(c.status || ""); setFClinician(c.clinician || "");
    setFCompliance(c.compliance || ""); setFPriority(c.priority || "");
    setAdv({ ...EMPTY_ADV, ...(c.adv || {}) });
    setRules(Array.isArray(c.rules) ? c.rules : []);
    if (c.sort) setSort(c.sort);
    setPage(1);
  };
  const resetView = () => { setActiveView("all"); setSearch(""); setFStatus(""); setFClinician(""); setFCompliance(""); setFPriority(""); setAdv(EMPTY_ADV); setRules([]); setSort({ key: "priority", dir: "desc" }); setPage(1); };

  const applyPreset = (p) => {
    resetView();
    if (p.set.status) setFStatus(p.set.status);
    if (p.set.compliance) setFCompliance(p.set.compliance);
    if (p.set.adv) {
      const next = { ...EMPTY_ADV };
      Object.entries(p.set.adv).forEach(([k, v]) => { next[k] = typeof v === "function" ? v() : v; });
      setAdv(next);
    }
    setRules((p.set.rules || []).map((r) => ({ ...r })));
  };

  const saveView = async () => {
    try {
      const v = await api.post("/saved-views", { page: "clients", name: viewName, config: { search, status: fStatus, clinician: fClinician, compliance: fCompliance, priority: fPriority, adv, rules, sort } });
      setViews((vs) => [...vs, v.data]); setActiveView(v.data.id); setSaveOpen(false); setViewName("");
      toast.success("View saved");
    } catch (e) { toast.error(apiError(e)); }
  };
  const deleteView = async (v) => {
    await api.delete(`/saved-views/${v.id}`).catch(() => {});
    setViews((vs) => vs.filter((x) => x.id !== v.id));
    if (activeView === v.id) resetView();
  };

  const clinicianName = (id) => { const u = clinicians.find((x) => x.id === id); return u ? `${u.first_name} ${u.last_name}` : ""; };

  const enriched = useMemo(() => (clients || []).map((c) => ({
    ...c,
    _score: compliance[c.id]?.score,
    _priority: priority[c.id]?.priority_points ?? 0,
    _plevel: priority[c.id]?.priority || "low",
    _reasons: priority[c.id]?.reasons || [],
    _clinician: clinicianName(c.clinician_id),
    _age: c.dob ? dayjs().diff(dayjs(c.dob), "year") : null,
    _program: clientPrograms[c.id] || "",
  })), [clients, compliance, priority, clinicians, clientPrograms]);

  const genderOpts = useMemo(() => [...new Set(enriched.map((c) => c.gender).filter(Boolean))], [enriched]);
  const activeRules = useMemo(() => rules.filter((r) => r.cond), [rules]);

  const filtered = useMemo(() => {
    let rows = enriched;
    if (search) { const s = search.toLowerCase(); rows = rows.filter((c) => `${c.first_name} ${c.last_name} ${c.mrn} ${c.health_record_number} ${c.email} ${c.phone}`.toLowerCase().includes(s)); }
    if (fStatus) rows = rows.filter((c) => c.status === fStatus);
    if (fClinician) rows = rows.filter((c) => c.clinician_id === fClinician);
    if (fCompliance === "red") rows = rows.filter((c) => (c._score ?? 100) < 50);
    if (fCompliance === "amber") rows = rows.filter((c) => (c._score ?? 100) >= 50 && (c._score ?? 100) < 80);
    if (fCompliance === "green") rows = rows.filter((c) => (c._score ?? 100) >= 80);
    if (fPriority) rows = rows.filter((c) => c._plevel === fPriority);
    if (adv.gender) rows = rows.filter((c) => c.gender === adv.gender);
    if (adv.program) rows = rows.filter((c) => c._program === adv.program);
    if (adv.ageMin !== "") rows = rows.filter((c) => c._age !== null && c._age >= Number(adv.ageMin));
    if (adv.ageMax !== "") rows = rows.filter((c) => c._age !== null && c._age <= Number(adv.ageMax));
    if (adv.admittedFrom) rows = rows.filter((c) => c.admission_date && c.admission_date >= adv.admittedFrom);
    if (adv.admittedTo) rows = rows.filter((c) => c.admission_date && c.admission_date <= adv.admittedTo);
    if (adv.lastSeenFrom) rows = rows.filter((c) => c.last_seen && c.last_seen.slice(0, 10) >= adv.lastSeenFrom);
    if (adv.lastSeenTo) rows = rows.filter((c) => c.last_seen && c.last_seen.slice(0, 10) <= adv.lastSeenTo);
    if (activeRules.length) {
      rows = rows.filter((c) => {
        const fl = flags[c.id] || {};
        let res = null;
        for (const r of activeRules) {
          const v = !!fl[r.cond];
          res = res === null ? v : (r.combo === "or" ? (res || v) : (res && v));
        }
        return res;
      });
    }
    const fn = SORTS[sort.key] || SORTS.name;
    rows = [...rows].sort((a, b) => { const va = fn(a), vb = fn(b); return (va < vb ? -1 : va > vb ? 1 : 0) * (sort.dir === "asc" ? 1 : -1); });
    return rows;
  }, [enriched, search, fStatus, fClinician, fCompliance, fPriority, adv, activeRules, flags, sort]);

  const advCount = Object.values(adv).filter((v) => v !== "").length + activeRules.length;
  const anyFilter = search || fStatus || fClinician || fCompliance || fPriority || advCount > 0;

  const stats = useMemo(() => {
    const monthStart = dayjs().startOf("month").format("YYYY-MM-DD");
    return [
      { key: "total", label: "Total Clients", value: enriched.length, icon: Users, tone: "text-[#4E342E] bg-[#F5F2EB]", apply: resetView },
      { key: "active", label: "Active", value: enriched.filter((c) => c.status === "active").length, icon: Activity, tone: "text-[#2E7D32] bg-[#E8F5E9]", apply: () => { resetView(); setFStatus("active"); } },
      { key: "high-priority", label: "High Priority", value: enriched.filter((c) => c._plevel === "high").length, icon: Flame, tone: "text-[#C62828] bg-[#FFEBEE]", apply: () => { resetView(); setFPriority("high"); } },
      { key: "critical-compliance", label: "Critical Compliance", value: enriched.filter((c) => (c._score ?? 100) < 50).length, icon: ShieldAlert, tone: "text-[#B84A00] bg-[#FFF3E0]", apply: () => { resetView(); setFCompliance("red"); }, tip: "Clients whose chart completeness score is below 50%. The score gives 25% each for complete demographics, a signed consent, a current treatment plan, and an intake on file." },
      { key: "admitted-month", label: "Admitted This Month", value: enriched.filter((c) => c.admission_date && c.admission_date >= monthStart).length, icon: CalendarPlus, tone: "text-[#1565C0] bg-[#E3F2FD]", apply: () => { resetView(); setAdv({ ...EMPTY_ADV, admittedFrom: monthStart }); } },
      { key: "discharged", label: "Discharged", value: enriched.filter((c) => c.status === "discharged").length, icon: LogOut, tone: "text-[#7B6157] bg-[#EFEBE0]", apply: () => { resetView(); setFStatus("discharged"); } },
    ];
  }, [enriched]); // eslint-disable-line react-hooks/exhaustive-deps

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const clickSort = (key) => { setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" })); setPage(1); };
  const SortIcon = ({ k }) => sort.key !== k ? <ArrowUpDown size={11} className="opacity-40" /> : sort.dir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />;
  const PLEVEL = { high: "bg-[#FFEBEE] text-[#C62828]", medium: "bg-[#FFF3E0] text-[#B84A00]", low: "bg-[#F5F2EB] text-[#7B6157]" };

  const header = (label, key) => (
    <th key={key} onClick={() => clickSort(key)} className="cursor-pointer select-none bg-[#FDFCF8] border-y border-[#EFEBE0] px-3 py-3 text-left text-xs font-semibold text-[#4E342E] whitespace-nowrap hover:bg-[#F5F2EB]" data-testid={`ac-sort-${key}`}>
      <span className="inline-flex items-center gap-1">{label} <SortIcon k={key} /></span>
    </th>
  );
  const setAdvField = (k) => (v) => { setAdv((s) => ({ ...s, [k]: v })); setPage(1); };
  const setRule = (i, patch) => setRules((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const clearAllFilters = () => { setAdv(EMPTY_ADV); setRules([]); setSearch(""); setFStatus(""); setFClinician(""); setFCompliance(""); setFPriority(""); };

  if (clients === null) return (
    <div className="animate-pulse space-y-4" data-testid="all-clients-loading">
      <div className="h-10 w-64 rounded-xl bg-[#EFEBE9]" />
      <div className="h-96 rounded-2xl bg-[#EFEBE9]" />
    </div>
  );

  return (
    <div data-testid="all-clients-page">
      <PageHeader title="All Clients" subtitle={`${filtered.length} of ${clients.length} clients · sorted by ${sort.key}`}
        action={<div className="flex items-center gap-2"><ExportCsvBtn resource="clients" /><Btn data-testid="ac-add-client-btn" onClick={() => navigate("/engagement/intake/new")}><Plus size={15} /> Add New Client</Btn></div>} />

      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6" data-testid="ac-stat-cards">
        {stats.map((s) => (
          <button key={s.key} type="button" data-testid={`ac-stat-${s.key}`} onClick={s.apply}
            className="rounded-2xl border border-[#EFEBE0] bg-white p-3.5 text-left transition-all hover:-translate-y-0.5 hover:shadow-md">
            <span className={`mb-1.5 flex h-8 w-8 items-center justify-center rounded-xl ${s.tone}`}><s.icon size={15} /></span>
            <div className="font-heading text-2xl font-bold text-[#3E2723]">{s.value}</div>
            <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-[#84675B]">{s.label}{s.tip && <InfoTip text={s.tip} />}</div>
          </button>
        ))}
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-1.5" data-testid="ac-view-tabs">
        <button data-testid="ac-view-all" onClick={resetView}
          className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${activeView === "all" ? "bg-[#4E342E] text-white" : "bg-white border border-[#EFEBE0] text-[#5D4037] hover:bg-[#F5F2EB]"}`}>
          All Clients
        </button>
        {views.map((v) => (
          <span key={v.id} className={`group inline-flex items-center gap-1 rounded-full pl-3.5 pr-1.5 py-1 text-xs font-bold transition-colors ${activeView === v.id ? "bg-[#4E342E] text-white" : "bg-white border border-[#EFEBE0] text-[#5D4037] hover:bg-[#F5F2EB]"}`}>
            <button data-testid={`ac-view-${v.id}`} onClick={() => applyView(v)}>{v.name}</button>
            <button data-testid={`ac-view-delete-${v.id}`} aria-label="Delete view" onClick={() => deleteView(v)}
              className={`flex h-5 w-5 items-center justify-center rounded-full ${activeView === v.id ? "hover:bg-white/20" : "hover:bg-[#EDE7DC]"}`}><X size={11} /></button>
          </span>
        ))}
        <button data-testid="ac-save-view-btn" onClick={() => setSaveOpen(true)}
          className="inline-flex items-center gap-1 rounded-full border border-dashed border-[#C9BEB0] px-3.5 py-1.5 text-xs font-bold text-[#7B6157] hover:bg-[#F5F2EB]">
          <Bookmark size={12} /> Save current view
        </button>
        <InfoTip text="Save any combination of search, filters, clinical conditions, and sorting as a personal reusable tab. Open the Filters drawer for the advanced builder with AND/OR conditions and preset templates." />
      </div>

      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center gap-2 p-4 border-b border-[#F1EEE7]">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#84675B]" />
            <input data-testid="ac-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Search name, record #, phone, email"
              className="h-10 w-64 rounded-lg border border-[#E5DFD3] pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#8D6E63]" />
          </div>
          <CocoaSelect testId="ac-filter-status" className="w-40" placeholder="All statuses" value={fStatus} onChange={(v) => { setFStatus(v); setPage(1); }} options={STATUS_OPTS} />
          <CocoaSelect testId="ac-filter-clinician" className="w-44" placeholder="All clinicians" value={fClinician} onChange={(v) => { setFClinician(v); setPage(1); }}
            options={clinicians.map((u) => [u.id, `${u.first_name} ${u.last_name}`])} />
          <CocoaSelect testId="ac-filter-compliance" className="w-48" placeholder="All compliance" value={fCompliance} onChange={(v) => { setFCompliance(v); setPage(1); }} options={COMPLIANCE_OPTS} />
          <CocoaSelect testId="ac-filter-priority" className="w-40" placeholder="All priorities" value={fPriority} onChange={(v) => { setFPriority(v); setPage(1); }} options={PRIORITY_OPTS} />
          <button data-testid="ac-open-filter-drawer" onClick={() => setDrawerOpen(true)}
            className="inline-flex h-10 items-center gap-1.5 rounded-lg border border-[#E5DFD3] px-3.5 text-xs font-bold text-[#4E342E] hover:bg-[#F5F2EB]">
            <SlidersHorizontal size={13} /> Filters{advCount > 0 && <span className="flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#4E342E] px-1 text-[9px] font-bold text-white" data-testid="ac-adv-count">{advCount}</span>}
          </button>
          {anyFilter && (
            <button data-testid="ac-clear-filters" onClick={resetView} className="text-xs font-bold text-[#7B6157] hover:underline">Clear all</button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr>
              {header("Client", "name")}{header("Record #", "hrn")}{header("Status", "status")}{header("Priority", "priority")}{header("Compliance", "compliance")}{header("Clinician", "clinician")}
              <th className="bg-[#FDFCF8] border-y border-[#EFEBE0] px-3 py-3 text-left text-xs font-semibold text-[#4E342E]">Phone</th>
              {header("Admitted", "admitted")}{header("Last Seen", "last_seen")}
              <th className="bg-[#FDFCF8] border-y border-[#EFEBE0] px-3 py-3" />
            </tr></thead>
            <tbody>
              {pageRows.map((c) => (
                <tr key={c.id} data-testid={`ac-row-${c.id}`} className="border-b border-[#F5F2EB] hover:bg-[#FDFCF8]">
                  <td className="px-3 py-2.5">
                    <Link to={`/clients/${c.id}/console`} data-testid={`ac-client-link-${c.id}`} className="flex items-center gap-2.5 min-w-0 group">
                      <Avatar c={c} />
                      <span className="min-w-0">
                        <span className="block truncate font-semibold text-[#3E2723] group-hover:underline">{c.first_name} {c.last_name}</span>
                        <span className="block truncate text-[11px] text-[#84675B]">{c._program || c.email}</span>
                      </span>
                    </Link>
                  </td>
                  <td className="px-3 py-2.5 tabular-nums text-[#6D4C41]">{c.health_record_number || c.mrn}</td>
                  <td className="px-3 py-2.5"><Badge value={c.status} /></td>
                  <td className="px-3 py-2.5">
                    <span data-testid={`ac-priority-${c.id}`}
                      onMouseEnter={(e) => { if (c._reasons.length) { const r = e.currentTarget.getBoundingClientRect(); setPriTip({ reasons: c._reasons, left: r.left, top: r.bottom + 6 }); } }}
                      onMouseLeave={() => setPriTip(null)}
                      className={`inline-flex cursor-help items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${PLEVEL[c._plevel]}`}>
                      {c._plevel === "high" && <Flame size={10} />}{c._plevel}
                    </span>
                  </td>
                  <td className="px-3 py-2.5"><ComplianceChip data={compliance[c.id]} testId={`ac-compliance-${c.id}`} /></td>
                  <td className="px-3 py-2.5 text-[#5D4037] whitespace-nowrap">{c._clinician || "-"}</td>
                  <td className="px-3 py-2.5 tabular-nums text-[#6D4C41] whitespace-nowrap">{c.phone}</td>
                  <td className="px-3 py-2.5 tabular-nums text-[#6D4C41] whitespace-nowrap">{c.admission_date ? dayjs(c.admission_date).format("MM/DD/YYYY") : "-"}</td>
                  <td className="px-3 py-2.5 tabular-nums text-[#6D4C41] whitespace-nowrap">{c.last_seen ? dayjs(c.last_seen).format("MM/DD/YYYY") : "-"}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex gap-1.5">
                      <button data-testid={`ac-quick-actions-${c.id}`} title="Check in / discharge" onClick={() => setQuickClient(c)}
                        className="flex h-7 w-7 items-center justify-center rounded-md bg-[#F5F2EB] text-[#6D4C41] hover:bg-[#2E7D32] hover:text-white transition-colors">
                        <Zap size={12} />
                      </button>
                      <button data-testid={`ac-open-console-${c.id}`} title="Open console" onClick={() => window.open(`/clients/${c.id}/console`, "_blank")}
                        className="flex h-7 w-7 items-center justify-center rounded-md bg-[#F5F2EB] text-[#6D4C41] hover:bg-[#4E342E] hover:text-white transition-colors">
                        <SquareArrowOutUpRight size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {pageRows.length === 0 && <Empty text="No clients match the current filters" />}
        </div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-[#F1EEE7] text-xs text-[#7B6157]">
          <span data-testid="ac-page-info">Page {page} of {pages} · {filtered.length} clients</span>
          <span className="flex gap-1.5">
            <Btn variant="outline" className="px-3 py-1.5 text-xs" data-testid="ac-prev-page" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Btn>
            <Btn variant="outline" className="px-3 py-1.5 text-xs" data-testid="ac-next-page" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Btn>
          </span>
        </div>
      </Card>

      <SlidePanel open={drawerOpen} onClose={() => setDrawerOpen(false)} title="Advanced Filters" subtitle="Combine basics, clinical conditions (AND/OR), and presets - then save as a view" width="max-w-md" testId="ac-filter-drawer">
        <div className="space-y-5 pb-16">
          <div>
            <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-[#84675B]"><Sparkles size={11} className="text-[#FFB300]" /> Preset templates
              <InfoTip text="One click applies a ready-made scenario: base filters plus clinical conditions. You can tweak anything afterward and save it as your own view." />
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="ac-presets">
              {PRESETS.map((p) => (
                <button key={p.key} data-testid={`ac-preset-${p.key}`} onClick={() => applyPreset(p)}
                  className="rounded-full border border-[#E5DFD3] bg-[#FDFCF8] px-3 py-1.5 text-[11px] font-bold text-[#5D4037] transition-colors hover:border-[#8D6E63] hover:bg-[#F5F2EB]">
                  {p.name}
                </button>
              ))}
            </div>
          </div>

          <div><Label>Search</Label><Input data-testid="acf-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Name, record #, phone, email" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Status</Label><CocoaSelect testId="acf-status" value={fStatus} onChange={(v) => { setFStatus(v); setPage(1); }} options={STATUS_OPTS} /></div>
            <div><Label>Clinician</Label><CocoaSelect testId="acf-clinician" value={fClinician} onChange={(v) => { setFClinician(v); setPage(1); }} options={clinicians.map((u) => [u.id, `${u.first_name} ${u.last_name}`])} /></div>
            <div><Label>Compliance</Label><CocoaSelect testId="acf-compliance" value={fCompliance} onChange={(v) => { setFCompliance(v); setPage(1); }} options={COMPLIANCE_OPTS} /></div>
            <div><Label>Priority</Label><CocoaSelect testId="acf-priority" value={fPriority} onChange={(v) => { setFPriority(v); setPage(1); }} options={PRIORITY_OPTS} /></div>
            <div><Label>Gender</Label><CocoaSelect testId="acf-gender" value={adv.gender} onChange={setAdvField("gender")} options={genderOpts} /></div>
            <div><Label>Program</Label><CocoaSelect testId="acf-program" value={adv.program} onChange={setAdvField("program")} options={programList} placeholder="All programs" /></div>
            <div><Label>Age min</Label><Input data-testid="acf-age-min" type="number" min="0" value={adv.ageMin} onChange={(e) => setAdvField("ageMin")(e.target.value)} /></div>
            <div><Label>Age max</Label><Input data-testid="acf-age-max" type="number" min="0" value={adv.ageMax} onChange={(e) => setAdvField("ageMax")(e.target.value)} /></div>
          </div>
          <div>
            <Label>Admitted between</Label>
            <div className="flex items-center gap-2">
              <DateField testId="acf-admitted-from" placeholder="From" value={adv.admittedFrom} onChange={setAdvField("admittedFrom")} className="flex-1" />
              <DateField testId="acf-admitted-to" placeholder="To" value={adv.admittedTo} onChange={setAdvField("admittedTo")} className="flex-1" />
            </div>
          </div>
          <div>
            <Label>Last seen between</Label>
            <div className="flex items-center gap-2">
              <DateField testId="acf-lastseen-from" placeholder="From" value={adv.lastSeenFrom} onChange={setAdvField("lastSeenFrom")} className="flex-1" />
              <DateField testId="acf-lastseen-to" placeholder="To" value={adv.lastSeenTo} onChange={setAdvField("lastSeenTo")} className="flex-1" />
            </div>
          </div>

          <div>
            <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-[#84675B]">Clinical conditions
              <InfoTip text="Chain conditions with AND/OR, evaluated top to bottom. Example: 'Not seen in 7+ days OR Missed an appointment' finds everyone needing re-engagement. Conditions use the same live data as the dashboard cards (allergies, injections, treatment plans due, and more)." />
            </div>
            <div className="space-y-2" data-testid="ac-rule-builder">
              {rules.map((r, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`ac-rule-row-${i}`}>
                  {i === 0 ? (
                    <span className="w-16 shrink-0 text-center text-[10px] font-bold uppercase tracking-wide text-[#84675B]">Where</span>
                  ) : (
                    <div className="flex w-16 shrink-0 overflow-hidden rounded-lg border border-[#E5DFD3] text-[10px] font-bold">
                      <button data-testid={`ac-rule-and-${i}`} onClick={() => setRule(i, { combo: "and" })}
                        className={`flex-1 py-1.5 ${r.combo !== "or" ? "bg-[#4E342E] text-white" : "bg-white text-[#7B6157] hover:bg-[#F5F2EB]"}`}>AND</button>
                      <button data-testid={`ac-rule-or-${i}`} onClick={() => setRule(i, { combo: "or" })}
                        className={`flex-1 py-1.5 ${r.combo === "or" ? "bg-[#FFB300] text-[#3E2723]" : "bg-white text-[#7B6157] hover:bg-[#F5F2EB]"}`}>OR</button>
                    </div>
                  )}
                  <CocoaSelect testId={`ac-rule-cond-${i}`} className="flex-1" placeholder="Pick a condition…" value={r.cond}
                    onChange={(v) => { setRule(i, { cond: v }); setPage(1); }} options={CONDITIONS} />
                  <button data-testid={`ac-rule-remove-${i}`} aria-label="Remove condition" onClick={() => { setRules((rs) => rs.filter((_, j) => j !== i)); setPage(1); }}
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[#84675B] hover:bg-[#FFEBEE] hover:text-[#C62828]"><X size={13} /></button>
                </div>
              ))}
              <button data-testid="ac-rule-add" onClick={() => setRules((rs) => [...rs, { combo: "and", cond: "" }])}
                className="flex items-center gap-1.5 rounded-lg border border-dashed border-[#C9BEB0] px-3 py-2 text-xs font-bold text-[#7B6157] hover:bg-[#F5F2EB]">
                <Plus size={12} /> Add condition
              </button>
            </div>
          </div>

          <p className="text-xs text-[#7B6157]">Filters apply live - the table behind updates as you set them. Matching now: <span className="font-bold text-[#3E2723]" data-testid="acf-match-count">{filtered.length}</span> client{filtered.length === 1 ? "" : "s"}.</p>
          <div className="sticky bottom-0 -mx-6 flex justify-between gap-2 border-t border-[#F1EEE7] bg-white px-6 py-4">
            <Btn variant="ghost" data-testid="acf-clear-btn" onClick={clearAllFilters}>Clear all</Btn>
            <div className="flex gap-2">
              <Btn variant="outline" data-testid="acf-save-view-btn" onClick={() => { setDrawerOpen(false); setSaveOpen(true); }}><Bookmark size={13} /> Save as view</Btn>
              <Btn data-testid="acf-done-btn" onClick={() => setDrawerOpen(false)}>Done</Btn>
            </div>
          </div>
        </div>
      </SlidePanel>

      <Modal open={saveOpen} onClose={() => setSaveOpen(false)} title="Save current view">
        <div className="space-y-4">
          <div><Label>View name</Label><Input data-testid="ac-view-name-input" value={viewName} onChange={(e) => setViewName(e.target.value)} placeholder="e.g. My high-priority caseload" /></div>
          <p className="text-xs text-[#7B6157]">Saves the current search, all filters, clinical conditions, and sorting as a reusable tab. Views are personal to your account.</p>
          <div className="flex justify-end gap-2"><Btn variant="ghost" onClick={() => setSaveOpen(false)}>Cancel</Btn><Btn data-testid="ac-view-save-confirm" onClick={saveView} disabled={!viewName.trim()}>Save View</Btn></div>
        </div>
      </Modal>
      <ClientQuickDrawer client={quickClient} open={!!quickClient} onClose={() => setQuickClient(null)}
        onUpdated={() => api.get("/clients").then((r) => setClients(r.data))} />
      {priTip && (
        <div className="pointer-events-none fixed z-[140] w-64 rounded-xl border border-[#EFEBE0] bg-white p-3 text-left shadow-xl" style={{ left: priTip.left, top: priTip.top }} data-testid="ac-priority-tooltip">
          {priTip.reasons.map((r, i) => <span key={i} className="block text-xs text-[#5D4037]">• {r}</span>)}
        </div>
      )}
    </div>
  );
}
